import { useEffect, useMemo, useRef, useState } from 'react';
import { rescheduleBooking, getRescheduleSlots } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { C } from '../utils/constants';
import { formatClockTime } from '../utils/formatTime';

// ─── Booking Hub configuration ───────────────────────────────────────────────
// Forked from BookingModal.jsx. Same native RY-branded picker + SlotPicker +
// stage machine, but it RESCHEDULES an existing booking instead of creating a
// new one: slots are fetched in reschedule mode (2h notice + 30-min spacing)
// with the client's own appointment excluded, and submit goes to the RY backend
// POST /api/user/reschedule (not booking-hub /api/public/bookings).
//
// Both calls are now JWT-gated RY backend routes, so this file holds no
// Booking Hub URL and no API key. It previously read VITE_RY_PORTAL_API_KEY,
// which Vite inlined into the public bundle.

// ─── Date helpers (identical to BookingModal) ────────────────────────────────
const TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return 'UTC'; }
})();

const pad = (n) => String(n).padStart(2, '0');
const localDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtTime = (d) => formatClockTime(d);
const fmtLongDate = (d) => d.toLocaleDateString(undefined, {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});
// Date + time together (the "From" row). Keep the date exactly as today; only
// the clock time goes through the shared formatter.
const fmtLongDateTime = (d) =>
  `${d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })}, ${formatClockTime(d)}`;
const mondayOf = (d) => {
  const day = d.getDay();
  const offset = (day + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
};

// ─── Component ───────────────────────────────────────────────────────────────

export const RescheduleModal = ({ booking, onClose, onRescheduled }) => {
  const { user } = useAuth();

  const [stage, setStage] = useState('loadingSlots');
  const [error, setError] = useState('');

  // Slots state — flat array of { start, end } as returned by the API.
  const [slots, setSlots] = useState([]);

  // User selection.
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const today = useMemo(() => new Date(), []);
  // Kept only so the SlotPicker prop signature matches BookingModal's.
  const [viewWeekStart, setViewWeekStart] = useState(() => mondayOf(today));

  // Floor: earliest a reschedule may land (first login + 7 days), from the
  // backend. Slots before it are hidden client-side; the server is the backstop.
  // If absent/unparseable → no client-side floor (server still guards).
  const floorMs = useMemo(() => {
    const v = booking?.rescheduleEarliest;
    const ms = v ? new Date(v).getTime() : NaN;
    return Number.isNaN(ms) ? null : ms;
  }, [booking?.rescheduleEarliest]);

  // Scroll-to-top when advancing to the confirmation step.
  const bodyScrollRef = useRef(null);
  useEffect(() => {
    if (stage === 'confirming' && bodyScrollRef.current) {
      bodyScrollRef.current.scrollTop = 0;
    }
  }, [stage]);

  // ─── Fetch slots (reschedule mode + exclude own appointment) ───────────────
  const fetchSlots = async () => {
    setStage('loadingSlots');
    setError('');
    try {
      // JWT-gated proxy. The backend checks the appointment is THIS user's,
      // reads the session type from it, and holds the Booking Hub key
      // server-side. axios rejects on non-2xx, so the old !res.ok branch is
      // the catch below.
      const { data } = await getRescheduleSlots(booking?.appointmentId || '');
      const fetched = Array.isArray(data?.slots) ? data.slots : [];
      setSlots(fetched);
      if (fetched.length === 0) {
        setError('There are no available times right now. Please try again later.');
        setStage('error');
        return;
      }
      setStage('pickingSlot');
    } catch (e) {
      // Prefer the backend's own message over axios's "Request failed with
      // status code N", which means nothing to a client.
      setError(
        e?.response?.data?.error ||
        e?.message ||
        'Could not load available times. Please try again.'
      );
      setStage('error');
    }
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Derived: slots grouped by local date key, dropping sub-floor slots ────
  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) {
      if (floorMs !== null && new Date(s.start).getTime() < floorMs) continue;
      const key = localDateKey(new Date(s.start));
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.start) - new Date(b.start));
    }
    return map;
  }, [slots, floorMs]);

  // ─── Submit reschedule (→ RY backend) ──────────────────────────────────────
  const submitReschedule = async () => {
    if (!selectedSlot || !booking?.appointmentId) return;
    setStage('submitting');
    setError('');
    try {
      // axios resolves only on 2xx; 200 { ok:true } → success.
      await rescheduleBooking({
        appointmentId: booking.appointmentId,
        start: selectedSlot.start,
        end: selectedSlot.end,
      });
      setStage('success');
      // Refresh the page's booking underneath so the new time shows immediately;
      // the success screen stays up until the user clicks Done (onClose).
      if (onRescheduled) onRescheduled();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 409) {
        // Floor violation OR slot taken between fetch and submit. Re-fetch and
        // bounce the user back to the picker with the server's message inline.
        // Set the notice AFTER fetchSlots (which clears error on entry).
        setSelectedSlot(null);
        await fetchSlots();
        setError(msg || 'That time is no longer available — please pick another.');
        return;
      }
      // 502 / 403 / network / other → error stage with a try-again affordance.
      setError(msg || 'Something went wrong rescheduling your session. Please try again.');
      setStage('error');
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const TryAgainButton = ({ label = 'Try again', onClick }) => (
    <button onClick={onClick} style={primaryBtn}>{label}</button>
  );

  // ─── Stage renders ────────────────────────────────────────────────────────
  let body;

  if (stage === 'loadingSlots') {
    body = (
      <CenterMessage>
        <Spinner />
        <p style={loadingText}>Loading available times…</p>
      </CenterMessage>
    );
  } else if (stage === 'error') {
    body = (
      <CenterMessage>
        <div style={errorIcon}>⚠️</div>
        <p style={{ ...errorText, marginBottom: 16 }}>{error}</p>
        <TryAgainButton onClick={fetchSlots} />
        <button
          onClick={onClose}
          style={{
            display: 'inline-block',
            marginTop: 12,
            background: 'none',
            border: 'none',
            color: C.terracotta,
            fontSize: '0.85rem',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Close
        </button>
      </CenterMessage>
    );
  } else if (stage === 'pickingSlot') {
    body = (
      <SlotPicker
        viewWeekStart={viewWeekStart}
        setViewWeekStart={setViewWeekStart}
        slotsByDate={slotsByDate}
        selectedDateKey={selectedDateKey}
        setSelectedDateKey={setSelectedDateKey}
        setSelectedSlot={setSelectedSlot}
        selectedSlot={selectedSlot}
        onContinue={() => setStage('confirming')}
        inlineNotice={error}
        today={today}
      />
    );
  } else if (stage === 'confirming') {
    body = (
      <RescheduleConfirm
        slot={selectedSlot}
        currentSessionDate={booking?.sessionDate}
        onBack={() => setStage('pickingSlot')}
        onConfirm={submitReschedule}
      />
    );
  } else if (stage === 'submitting') {
    body = (
      <CenterMessage>
        <Spinner />
        <p style={loadingText}>Rescheduling your session…</p>
      </CenterMessage>
    );
  } else if (stage === 'success') {
    const start = new Date(selectedSlot.start);
    body = (
      <CenterMessage>
        <div style={{ fontSize: '3rem', marginBottom: 8 }}>✓</div>
        <h3 style={successHeading}>Your session has been rescheduled</h3>
        <p style={{ ...loadingText, marginBottom: 4 }}>{fmtLongDate(start)}</p>
        <p style={{ ...loadingText, marginBottom: 20 }}>at {fmtTime(start)}</p>
        <p style={{ ...subtleText, marginBottom: 8 }}>
          Your Zoom link is unchanged.
        </p>
        <p style={{ ...subtleText, marginBottom: 20 }}>
          {user?.email
            ? `We've emailed your updated confirmation to ${user.email}.`
            : "We've emailed your updated confirmation."}
        </p>
        <button onClick={onClose} style={primaryBtn}>Done</button>
      </CenterMessage>
    );
  }

  // ─── Outer chrome — mirrors BookingModal ──────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 680,
        height: '95vh',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        animation: 'fadeInUp 0.3s ease both',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
          padding: '24px 28px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontFamily: "'Forum', serif",
              fontSize: '0.72rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: 6,
            }}>
              🔄 RESCHEDULE
            </div>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: '#fff',
              fontSize: '1.5rem',
              letterSpacing: '0.03em',
              marginBottom: 6,
            }}>
              Choose a new time for your session
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
              Breakthrough Mapping Session · With Ben · 90 minutes · Via Zoom
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.1rem',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div ref={bodyScrollRef} style={{ flex: 1, overflow: 'auto', minHeight: 0, background: '#fff' }}>
          {body}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components (copied from BookingModal for an independent fork) ────────

const CenterMessage = ({ children }) => (
  <div style={{
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 28px',
    textAlign: 'center',
  }}>
    {children}
  </div>
);

const Spinner = () => (
  <div
    style={{
      width: 36,
      height: 36,
      border: `3px solid ${C.veryLightBrown}`,
      borderTopColor: C.gold,
      borderRadius: '50%',
      animation: 'rySpin 0.9s linear infinite',
      marginBottom: 16,
    }}
  >
    <style>{`@keyframes rySpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const SlotPicker = ({
  slotsByDate,
  selectedDateKey, setSelectedDateKey,
  selectedSlot, setSelectedSlot,
  onContinue,
  inlineNotice,
}) => {
  const dateKeys = Object.keys(slotsByDate).sort();
  const PAGE_SIZE = 7;
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [slotsByDate]);

  const totalPages = Math.max(1, Math.ceil(dateKeys.length / PAGE_SIZE));
  const canPrev    = pageIndex > 0;
  const canNext    = pageIndex < totalPages - 1;
  const pageSlice  = dateKeys.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  const timesForSelected = selectedDateKey ? slotsByDate[selectedDateKey] || [] : [];

  const noticeRef = useRef(null);
  useEffect(() => {
    if (inlineNotice && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [inlineNotice]);

  return (
    <div style={{ padding: '24px 28px' }}>
      {inlineNotice && (
        <div
          ref={noticeRef}
          role="alert"
          style={{
            background: 'rgba(184,52,24,0.08)',
            border: `1px solid rgba(184,52,24,0.4)`,
            borderLeft: '4px solid #B83418',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 18,
            color: '#B83418',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <span aria-hidden="true" style={{ flexShrink: 0, fontSize: '1.2rem', lineHeight: 1.25 }}>⚠</span>
          <span style={{ flex: 1 }}>{inlineNotice}</span>
        </div>
      )}

      {/* Page nav — arrows only */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button
          onClick={() => canPrev && setPageIndex(p => p - 1)}
          disabled={!canPrev}
          style={navBtn(canPrev)}
          aria-label="Previous dates"
        >
          ‹
        </button>
        <button
          onClick={() => canNext && setPageIndex(p => p + 1)}
          disabled={!canNext}
          style={navBtn(canNext)}
          aria-label="Next dates"
        >
          ›
        </button>
      </div>

      {/* Available day chips */}
      <div style={availDayGrid}>
        {pageSlice.map((key) => {
          const d = new Date(key + 'T12:00:00');
          const isSelected = selectedDateKey === key;
          return (
            <button
              key={key}
              onClick={() => {
                setSelectedDateKey(key);
                setSelectedSlot(null);
              }}
              style={availDayChip(isSelected)}
              aria-label={fmtLongDate(d)}
            >
              <span style={{ ...chipWeekday, color: isSelected ? '#fff' : C.brown }}>
                {d.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span style={{ ...chipDate, color: isSelected ? 'rgba(255,255,255,0.85)' : C.terracotta }}>
                {d.toLocaleDateString(undefined, { month: 'short' })} {d.getDate()}
              </span>
              {!isSelected && <span style={dot(true)} />}
            </button>
          );
        })}
      </div>

      {/* Times */}
      <div style={{ marginTop: 22 }}>
        {!selectedDateKey ? (
          <p style={{ ...subtleText, textAlign: 'center', marginTop: 4 }}>
            Pick a date
          </p>
        ) : timesForSelected.length === 0 ? (
          <p style={{ ...subtleText, textAlign: 'center', marginTop: 4 }}>
            No times available for this date.
          </p>
        ) : (
          <>
            <div style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '0.95rem',
              marginBottom: 10,
              letterSpacing: '0.04em',
            }}>
              {fmtLongDate(new Date(selectedDateKey))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {timesForSelected.map(s => {
                const isPicked = selectedSlot?.start === s.start;
                return (
                  <button
                    key={s.start}
                    onClick={() => setSelectedSlot(s)}
                    style={timeChip(isPicked)}
                  >
                    {fmtTime(new Date(s.start))}
                  </button>
                );
              })}
            </div>
            <p style={{ ...subtleText, marginTop: 12, fontSize: '0.78rem' }}>
              Times shown in your timezone ({TZ}).
            </p>
          </>
        )}
      </div>

      {/* Continue */}
      {selectedSlot && (
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <button onClick={onContinue} style={primaryBtn}>
            Continue →
          </button>
        </div>
      )}
    </div>
  );
};

const RescheduleConfirm = ({ slot, currentSessionDate, onBack, onConfirm }) => {
  const start = new Date(slot.start);
  const end = new Date(slot.end);
  const current = currentSessionDate ? new Date(currentSessionDate) : null;
  return (
    <div style={{ padding: '28px' }}>
      <h3 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.3rem',
        letterSpacing: '0.03em',
        marginBottom: 6,
      }}>
        Confirm new time
      </h3>
      <p style={{ ...subtleText, marginBottom: 20 }}>
        Please check the new time below, then confirm.
      </p>

      <div style={{
        background: C.veryLightBrown,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 20,
      }}>
        <Row label="Session" value="Breakthrough Mapping Session" />
        {current && (
          <Row
            label="From"
            value={fmtLongDateTime(current)}
            strike
          />
        )}
        <Row label="To"       value={`${fmtLongDate(start)}`} />
        <Row label="Time"     value={`${fmtTime(start)} – ${fmtTime(end)}`} />
        <Row label="Duration" value="90 minutes" last />
      </div>

      <p style={{ ...subtleText, marginBottom: 24 }}>
        Your Zoom link stays the same.
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button onClick={onBack} style={secondaryBtn}>← Back</button>
        <button onClick={onConfirm} style={primaryBtn}>Confirm new time</button>
      </div>
    </div>
  );
};

const Row = ({ label, value, last = false, strike = false }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 16,
    padding: '6px 0',
    borderBottom: last ? 'none' : `1px solid ${C.border}`,
    fontSize: '0.92rem',
  }}>
    <span style={{ color: C.terracotta, fontSize: '0.82rem', letterSpacing: '0.04em' }}>
      {label}
    </span>
    <span style={{
      color: strike ? C.terracotta : C.brown,
      fontFamily: "'Forum', serif",
      letterSpacing: '0.02em',
      textAlign: 'right',
      textDecoration: strike ? 'line-through' : 'none',
      opacity: strike ? 0.7 : 1,
    }}>
      {value}
    </span>
  </div>
);

// ─── Styles (copied from BookingModal) ───────────────────────────────────────

const primaryBtn = {
  fontFamily: "'Forum', serif",
  background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
  border: 'none',
  color: C.darkBrown,
  padding: '12px 24px',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: '0.03em',
  boxShadow: '0 3px 12px rgba(245,166,35,0.35)',
};

const secondaryBtn = {
  fontFamily: "'Forum', serif",
  background: '#fff',
  border: `1.5px solid ${C.border}`,
  color: C.brown,
  padding: '12px 22px',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: '0.03em',
};

const navBtn = (enabled) => ({
  background: enabled ? C.veryLightBrown : 'transparent',
  border: `1px solid ${enabled ? C.border : 'transparent'}`,
  color: enabled ? C.brown : 'rgba(107,62,10,0.25)',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: enabled ? 'pointer' : 'default',
  fontSize: '1.2rem',
  fontFamily: "'Forum', serif",
});

const availDayGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 6,
  marginBottom: 4,
};

const availDayChip = (selected) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 2,
  padding: '10px 2px 16px',
  background: selected ? `linear-gradient(135deg, ${C.amber}, ${C.gold})` : '#fff',
  border: `1.5px solid ${selected ? C.gold : C.border}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s',
  boxShadow: selected ? '0 2px 8px rgba(245,166,35,0.35)' : 'none',
});

const chipWeekday = {
  fontFamily: "'Forum', serif",
  fontSize: '0.98rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1.15,
};

const chipDate = {
  fontSize: '0.62rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
};

const dot = (enabled) => ({
  position: 'absolute',
  bottom: 4,
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: enabled ? C.gold : 'transparent',
});

const timeChip = (selected) => ({
  fontFamily: "'Forum', serif",
  background: selected ? `linear-gradient(135deg, ${C.amber}, ${C.gold})` : '#fff',
  border: `1.5px solid ${selected ? C.gold : C.border}`,
  color: selected ? '#fff' : C.brown,
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: '0.92rem',
  cursor: 'pointer',
  letterSpacing: '0.02em',
  minWidth: 78,
  textAlign: 'center',
  boxShadow: selected ? '0 2px 8px rgba(245,166,35,0.35)' : 'none',
});

const loadingText = {
  color: C.brown,
  fontSize: '0.95rem',
  fontFamily: "'Forum', serif",
  letterSpacing: '0.03em',
};

const subtleText = {
  color: C.terracotta,
  fontSize: '0.85rem',
};

const successHeading = {
  fontFamily: "'Forum', serif",
  color: C.brown,
  fontSize: '1.4rem',
  letterSpacing: '0.03em',
  marginBottom: 10,
};

const errorIcon = {
  fontSize: '2.2rem',
  marginBottom: 8,
};

const errorText = {
  color: C.brown,
  fontSize: '0.95rem',
  lineHeight: 1.5,
  maxWidth: 420,
};
